import React from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";

/**
 * Shared cancel dialog for the iteration flow pages (Geometric, Width,
 * Color). It replaces the original Cancel button, which exited without
 * any confirmation, with an explicit two-option choice:
 *
 *   - "Continue editing"     -> close dialog, stay on current page
 *   - "Discard ... and exit" -> caller-supplied async cleanup, then leave
 *
 * The destructive action is intentionally on the right (red contained),
 * mirroring the delete-confirmation pattern used elsewhere in the app.
 * The safe default ("Continue editing") is on the left and matches the
 * primary blue contained style used by the rest of the iteration UI.
 *
 * Props:
 *   open            : boolean
 *   onClose         : () => void                — close without leaving
 *   onDiscardAndExit: () => Promise<void>|void  — clean up DB, then exit
 *   summary         : string (optional)         — e.g. "You've measured 2 of 5 patch groups."
 *   stepLabel       : string (optional)         — e.g. "cluster width measurement"
 *   discardLabel    : string (optional)         — overrides default destructive button text
 *   busy            : boolean (optional)        — disables actions while async work runs
 */
export default function IterationExitDialog({
    open,
    onClose,
    onDiscardAndExit,
    summary,
    stepLabel = "this iteration's work",
    discardLabel,
    busy = false,
}) {
    const [discarding, setDiscarding] = React.useState(false);
    const isBusy = busy || discarding;

    const handleDiscard = async () => {
        if (typeof onDiscardAndExit !== "function") return;
        setDiscarding(true);
        try {
            await onDiscardAndExit();
        } finally {
            setDiscarding(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isBusy ? undefined : onClose}
            aria-labelledby="iteration-exit-dialog-title"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="iteration-exit-dialog-title">
                Leave this iteration?
            </DialogTitle>
            <DialogContent>
                <Stack spacing={1.5}>
                    {summary ? (
                        <Typography variant="body2" color="text.primary">
                            {summary}
                        </Typography>
                    ) : null}
                    <DialogContentText>
                        Either continue editing this step, or discard{" "}
                        {stepLabel} for this iteration. Discarding clears the
                        measurements you've taken so far and returns you to
                        the explorations list; the next time you continue,
                        you'll start fresh from Geometric Measurement.
                    </DialogContentText>
                    <DialogContentText sx={{ fontSize: "0.85rem" }}>
                        Choose <strong>Discard</strong> if you uploaded the wrong
                        image (e.g. a wrong cluster photograph). Continuing on
                        top of bad measurements means later iterations will be
                        built from those values, and cleaning that up afterwards
                        is much more work than starting this iteration over now.
                    </DialogContentText>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, pb: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={isBusy}
                    autoFocus
                    sx={{ textTransform: "none", fontWeight: 600 }}
                >
                    Continue editing
                </Button>
                <Button
                    onClick={handleDiscard}
                    variant="contained"
                    color="error"
                    disableElevation
                    disabled={isBusy || typeof onDiscardAndExit !== "function"}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                    startIcon={
                        discarding ? (
                            <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} />
                        ) : null
                    }
                >
                    {discardLabel || "Discard and exit"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
